import { useState } from "react";
import { Link } from "react-router-dom";
import { Hash, PlayCircle, Loader2, AlertCircle, ArrowUpRight, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { toast } from "sonner";


function StatusPill({ statuses }) {
  const allSent = statuses?.every?.(s => s === "sent");
  const anyFailed = statuses?.some?.(s => s === "failed");
  const map = {
    sent:      { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
    generated: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Generated" },
    failed:    { bg: "#FEF2F2", fg: "#B91C1C", label: "Failed" },
  };
  const key = anyFailed ? "failed" : allSent ? "sent" : "generated";
  const m = map[key];
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}


async function fetchWeeks() {
  const r = await fetch(`${API}/weekly-numbers?limit=20`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}


export default function WeeklyNumbersList() {
  const [generating, setGenerating] = useState(false);
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["weekly-numbers"],
    queryFn: fetchWeeks,
    staleTime: 5 * 60_000,
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/weekly-numbers/generate`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ dry_run: false, send_email: false }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      toast.success(`Numbers generation started (job ${body.job_id}). Runs ~60-120s.`);
      setTimeout(() => refetch(), 10000);
    } catch (e) {
      toast.error(`Generate failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="weekly-numbers-page">
      <PageHeader
        title="Weekly By The Numbers"
        subtitle="Wed→Tue SGT window — 3 single-stat PNG cards per week. Auto-delivered every Wednesday 08:00 SGT."
      />
      <div className="hm-surface p-4 md:p-6" data-testid="weekly-numbers-panel">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="text-xs text-hm-fg-muted leading-relaxed max-w-xl">
            Product 6 · Weekly By The Numbers. Three branded cards, each one hard
            number tied to a named company with a verified source. Tier 1 → 2 → 3
            cascade. 365-day deduplication.
          </div>
          <button
            onClick={generateNow}
            disabled={generating}
            data-testid="weekly-numbers-generate-now"
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim disabled:opacity-40"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            Generate now
          </button>
        </div>

        {isError && !data ? (
          <div className="p-6 rounded-lg border border-hm-border bg-hm-bg-1 flex items-start gap-4"
               data-testid="weekly-numbers-error">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-hm-fg font-medium">Unable to load weeks right now</div>
              <p className="mono text-[11px] text-hm-fg-muted mt-1">
                {error?.message || "Transient connection failure."}
              </p>
              <button onClick={() => refetch()} disabled={isFetching}
                      className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-hm-border text-hm-fg hover:border-hm-border-strong disabled:opacity-40">
                {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Retry
              </button>
            </div>
          </div>
        ) : rows.length === 0 && !isPending ? (
          <div className="py-10 text-center" data-testid="weekly-numbers-empty">
            <Hash className="h-6 w-6 text-hm-fg-subtle mx-auto mb-3" />
            <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No cards yet. Click &quot;Generate now&quot; above.
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-hm-border overflow-hidden">
            <div className="grid grid-cols-[1fr_0.5fr_0.6fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              <div>Week</div>
              <div>Cards</div>
              <div>Status</div>
              <div>Created</div>
              <div></div>
            </div>
            <div className="divide-y divide-hm-border">
              {rows.map((r) => (
                <Link key={r.week_date}
                      to={`/weekly-numbers/${r.week_date}`}
                      data-testid={`weekly-numbers-row-${r.week_date}`}
                      className="grid grid-cols-[1fr_0.5fr_0.6fr_0.9fr_auto] gap-4 px-5 py-4 items-center hover:bg-hm-bg-1/40 transition-colors">
                  <div className="text-sm text-hm-fg font-medium">{r.week_date}</div>
                  <div className="mono text-xs text-hm-fg">{r.cards}/3</div>
                  <div><StatusPill statuses={r.statuses} /></div>
                  <div className="mono text-[11px] text-hm-fg-muted">
                    {new Date(r.created_at).toLocaleString(undefined, {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                  <div className="text-hm-fg-subtle"><ArrowUpRight className="h-4 w-4" /></div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
